/**
 * Global portfolio filter context
 *
 * Provides a global portfolio selection that persists across navigation
 * and page refreshes. All monitoring views (Dashboard, Holdings, Analytics,
 * ThesisAllocations) respect this selection.
 *
 * Pattern follows AuthProvider exactly.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { usePortfolios } from '../hooks/usePortfolios'
import type { Portfolio } from '../types/portfolios'

const STORAGE_KEY = 'portfolio-tracker-selected-portfolio'

interface PortfolioFilterContextType {
  /** Selected portfolio ID, or null for "All Portfolios" */
  selectedPortfolioId: string | null
  /** Selected portfolio object, or null if "All Portfolios" or loading */
  selectedPortfolio: Portfolio | null
  /** All available portfolios */
  portfolios: Portfolio[]
  /** Select a portfolio by ID, or null for "All Portfolios" */
  selectPortfolio: (id: string | null) => void
  /** True while loading portfolios */
  isLoading: boolean
}

const PortfolioFilterContext = createContext<PortfolioFilterContextType | undefined>(undefined)

interface PortfolioFilterProviderProps {
  children: ReactNode
}

export function PortfolioFilterProvider({ children }: PortfolioFilterProviderProps) {
  const { data: portfolios = [], isLoading: portfoliosLoading } = usePortfolios()
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(() => {
    // Restore from localStorage on mount
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? stored : null
  })

  // Validate stored selection against available portfolios
  useEffect(() => {
    if (!portfoliosLoading && portfolios.length > 0 && selectedPortfolioId) {
      const exists = portfolios.some(p => p.portfolio_id === selectedPortfolioId)
      if (!exists) {
        // Stored portfolio no longer exists, reset to "All"
        setSelectedPortfolioId(null)
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [portfolios, portfoliosLoading, selectedPortfolioId])

  function selectPortfolio(id: string | null) {
    setSelectedPortfolioId(id)
    if (id) {
      localStorage.setItem(STORAGE_KEY, id)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  const selectedPortfolio = selectedPortfolioId
    ? portfolios.find(p => p.portfolio_id === selectedPortfolioId) || null
    : null

  const value: PortfolioFilterContextType = {
    selectedPortfolioId,
    selectedPortfolio,
    portfolios,
    selectPortfolio,
    isLoading: portfoliosLoading,
  }

  return (
    <PortfolioFilterContext.Provider value={value}>
      {children}
    </PortfolioFilterContext.Provider>
  )
}

export function usePortfolioFilter() {
  const context = useContext(PortfolioFilterContext)
  if (context === undefined) {
    throw new Error('usePortfolioFilter must be used within a PortfolioFilterProvider')
  }
  return context
}
